/**
 * Attendance (owner, 13 Oct 2026: "design a attendance system inside my
 * employee portal login").
 *
 * Every employee marks their own day from their sign-in — "Mark in" when
 * they arrive, "Mark out" when they leave — and the phone's location rides
 * along when it is shared. The owner's head-admin desks read everyone's day
 * and month, set a day right by hand (with a reason), answer leave, and keep
 * the holiday list and the rules. A day nobody marked and nobody explained
 * reads as absent, but only from the day attendance began and only on a
 * working day: Sundays and holidays are never absences.
 *
 * Both sides read these rules, so the menu, the reminder bar and the server
 * agree on who marks, who manages, what a day is called and which day it is.
 */
import { isBookHead } from './book-wall.js';
import { HEAD_ADMIN_DESK_EMAILS, isVerifiedEmployee, type PersonRuleInput } from './domain.js';

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

export const ATTENDANCE_STATUSES = ['PRESENT', 'HALF_DAY', 'ABSENT', 'LEAVE', 'UNPAID_LEAVE', 'HOLIDAY', 'WEEK_OFF'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  HALF_DAY: 'Half day',
  ABSENT: 'Absent',
  LEAVE: 'Paid leave',
  UNPAID_LEAVE: 'Unpaid leave',
  HOLIDAY: 'Holiday',
  WEEK_OFF: 'Week off',
};

/** The register codes, the way Salary Box writes them (13 Oct 2026, "replica of salary box"): P · A · HD · PL · UL · WO · H. */
export const ATTENDANCE_STATUS_CODES: Record<AttendanceStatus, string> = {
  PRESENT: 'P',
  HALF_DAY: 'HD',
  ABSENT: 'A',
  LEAVE: 'PL',
  UNPAID_LEAVE: 'UL',
  HOLIDAY: 'H',
  WEEK_OFF: 'WO',
};

/** What the office may write on a day by hand — Salary Box's one-tap buttons. Holidays and week-offs come from the calendar, not a correction. */
export const OFFICE_SET_STATUSES = ['PRESENT', 'ABSENT', 'HALF_DAY', 'LEAVE', 'UNPAID_LEAVE'] as const;
export type OfficeSetStatus = (typeof OFFICE_SET_STATUSES)[number];

/** Who wrote the day: the person's own mark, the office's hand, or an approved leave. A calendar day (holiday, week off) has none. */
export const ATTENDANCE_SOURCES = ['SELF', 'OFFICE', 'LEAVE'] as const;
export type AttendanceSource = (typeof ATTENDANCE_SOURCES)[number];

export const LEAVE_KINDS = ['CASUAL', 'SICK', 'UNPAID', 'OTHER'] as const;
export type LeaveKind = (typeof LEAVE_KINDS)[number];
export const LEAVE_KIND_LABELS: Record<LeaveKind, string> = { CASUAL: 'Casual leave', SICK: 'Sick leave', UNPAID: 'Unpaid leave', OTHER: 'Other (paid)' };

/** What an approved leave writes on the day: unpaid leave is the one kind the salary does not pay. */
export function leaveStatusOf(kind: LeaveKind): 'LEAVE' | 'UNPAID_LEAVE' {
  return kind === 'UNPAID' ? 'UNPAID_LEAVE' : 'LEAVE';
}

export const LEAVE_STATUSES = ['REQUESTED', 'APPROVED', 'DECLINED', 'CANCELLED'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  REQUESTED: 'Waiting',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  CANCELLED: 'Taken back',
};

export const LEAVE_REASON_MAX = 400;
export const ATTENDANCE_NOTE_MAX = 400;
export const HOLIDAY_NAME_MAX = 80;
/** The longest span one leave request may cover, in calendar days. */
export const LEAVE_MAX_DAYS = 60;

// ---------------------------------------------------------------------------
// Who
// ---------------------------------------------------------------------------

/**
 * Who marks attendance: every full-time employee — the office, the desks, the
 * ASMs, the factory hands — never a mitr, outlet or dealer's counter sign-in,
 * never a head-admin desk, which is the owner's door. Drivers are not on it (owner, 20 Oct 2026: "remove drivers
 * from the attendence they are not full time employees"), and since 5 Nov 2026 they have no sign-in at all.
 */
export function marksAttendance(user: PersonRuleInput): boolean {
  return isVerifiedEmployee(user);
}

/** One person's office timing as the register shows it: their own where set, else the company's. */
export interface PersonTiming {
  user: { id: string; name: string; role: string; book: string; branch?: string | null };
  /** Their own times and week off; null fields follow the company's. */
  shiftStart: string | null;
  shiftEnd: string | null;
  weeklyOff: number[] | null;
}

/**
 * Who keeps the register — marks the day for everyone, corrects it, answers
 * leave, sets holidays and the rules: the owner's head-admin desks, and
 * anyone the owner switched on as an attendance admin from Users (14 Oct
 * 2026: "give surbhi access to attendance admin").
 */
export function managesAttendance(user: { email: string; organizationId?: string; attendanceAdmin?: boolean | null } | null | undefined): boolean {
  if (!user) return false;
  if (HEAD_ADMIN_DESK_EMAILS.includes(user.email.trim().toLowerCase()) || user.attendanceAdmin === true) return true;
  // A walled book's head keeps that book's register (FAB EV, 29 Oct 2026) — attendance.service holds him to it.
  return user.organizationId !== undefined && isBookHead({ email: user.email, organizationId: user.organizationId });
}

/** Who sets salaries, keeps the ledger, pays and reads anyone's payslip: the head-admin desks alone. */
export function managesSalary(user: { email: string } | null | undefined): boolean {
  return Boolean(user) && HEAD_ADMIN_DESK_EMAILS.includes(user!.email.trim().toLowerCase());
}

/** One person's own shift and week off, where set on their salary card. */
export interface PersonRules {
  shiftStart?: string | null;
  shiftEnd?: string | null;
  weeklyOff?: number[] | null;
}

/** The company's rules with one person's own shift and week off laid over them — late, week offs and overtime read from these. */
export function effectivePolicy(policy: AttendancePolicy, own: PersonRules | null | undefined): AttendancePolicy {
  if (!own) return policy;
  return {
    ...policy,
    opensAt: own.shiftStart && CLOCK_RE.test(own.shiftStart) ? own.shiftStart : policy.opensAt,
    closesAt: own.shiftEnd && CLOCK_RE.test(own.shiftEnd) ? own.shiftEnd : policy.closesAt,
    weeklyOff: own.weeklyOff ?? policy.weeklyOff,
  };
}

// ---------------------------------------------------------------------------
// The rules
// ---------------------------------------------------------------------------

export const DAY_METHODS = ['CALENDAR', 'FIXED_30', 'FIXED_26'] as const;
export type DayMethod = (typeof DAY_METHODS)[number];
/** Salary Box's three ways of turning a monthly salary into a day's pay. */
export const DAY_METHOD_LABELS: Record<DayMethod, string> = {
  CALENDAR: 'Calendar month',
  FIXED_30: '30-day month',
  FIXED_26: '26-day month',
};
export const DAY_METHOD_HINTS: Record<DayMethod, string> = {
  CALENDAR: 'Salary ÷ the days in the month (31 in October). Week offs and holidays are paid.',
  FIXED_30: 'Salary ÷ 30, whatever the month. Week offs and holidays are paid.',
  FIXED_26: 'Salary ÷ 26, for staff with a weekly off. Week offs are not paid separately; holidays are.',
};

export interface AttendancePolicy {
  /** When the office opens, "HH:MM" in India. */
  opensAt: string;
  /** When the shift ends, "HH:MM" in India — hours past it are overtime when the office records them. */
  closesAt: string;
  /** Minutes after opening before a mark-in counts as late. */
  lateAfterMinutes: number;
  /** Weekly off days, 0 = Sunday … 6 = Saturday. */
  weeklyOff: number[];
  /** The first day attendance is kept, "YYYY-MM-DD". Days before it are not absences. */
  startsOn: string;
  /** How a monthly salary becomes a day's pay (Salary Box's three methods). */
  dayMethod: DayMethod;
}

export const DEFAULT_ATTENDANCE_POLICY: AttendancePolicy = {
  opensAt: '10:00',
  closesAt: '18:00',
  lateAfterMinutes: 15,
  weeklyOff: [0],
  // Shipped on 13 Oct 2026 in the afternoon; the first full day is the next one.
  startsOn: '2026-10-14',
  dayMethod: 'CALENDAR',
};

export const CLOCK_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const MONTH_KEY_RE = /^\d{4}-\d{2}$/;

/** A stored policy, however old or partial, read into a whole one. */
export function readAttendancePolicy(raw: unknown): AttendancePolicy {
  const base = { ...DEFAULT_ATTENDANCE_POLICY, weeklyOff: [...DEFAULT_ATTENDANCE_POLICY.weeklyOff] };
  if (!raw || typeof raw !== 'object') return base;
  const value = raw as Record<string, unknown>;
  if (typeof value.opensAt === 'string' && CLOCK_RE.test(value.opensAt)) base.opensAt = value.opensAt;
  if (typeof value.closesAt === 'string' && CLOCK_RE.test(value.closesAt)) base.closesAt = value.closesAt;
  if (typeof value.dayMethod === 'string' && (DAY_METHODS as readonly string[]).includes(value.dayMethod)) base.dayMethod = value.dayMethod as DayMethod;
  if (typeof value.lateAfterMinutes === 'number' && Number.isInteger(value.lateAfterMinutes) && value.lateAfterMinutes >= 0 && value.lateAfterMinutes <= 600) {
    base.lateAfterMinutes = value.lateAfterMinutes;
  }
  if (Array.isArray(value.weeklyOff)) {
    base.weeklyOff = [...new Set(value.weeklyOff.filter((d): d is number => typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= 6))].sort();
  }
  if (typeof value.startsOn === 'string' && DAY_KEY_RE.test(value.startsOn)) base.startsOn = value.startsOn;
  return base;
}

// ---------------------------------------------------------------------------
// Days — the office's own day (IST)
// ---------------------------------------------------------------------------

export const IST_OFFSET_MS = 330 * 60_000;

/** The IST calendar day an instant falls on, "YYYY-MM-DD". */
export function istDayKeyOf(at: Date): string {
  return new Date(at.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** The IST clock at an instant, "HH:MM". */
export function istClockOf(at: Date): string {
  return new Date(at.getTime() + IST_OFFSET_MS).toISOString().slice(11, 16);
}

/** Minutes since midnight of an "HH:MM". */
export function clockMinutes(clock: string): number {
  const [hh, mm] = clock.split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
}

/** 0 = Sunday … 6 = Saturday, for a day key. */
export function weekdayOfKey(key: string): number {
  return new Date(`${key}T00:00:00Z`).getUTCDay();
}

export function shiftDayKey(key: string, by: number): string {
  return new Date(new Date(`${key}T00:00:00Z`).getTime() + by * 86_400_000).toISOString().slice(0, 10);
}

/** Every day of a month, "YYYY-MM" → ["YYYY-MM-01", …]. */
export function daysOfMonth(month: string): string[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** Every day from one key to another, both included. Empty when the span is backwards or longer than a year. */
export function daysBetweenKeys(from: string, to: string): string[] {
  if (from > to) return [];
  const out: string[] = [];
  for (let key = from; key <= to && out.length <= 366; key = shiftDayKey(key, 1)) out.push(key);
  return out;
}

export function shiftMonthKey(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + by, 1)).toISOString().slice(0, 7);
}

/** What the calendar says about a day before anyone marks it: a holiday by name, the week off, or nothing. */
export function calendarStatusOf(key: string, policy: AttendancePolicy, holidays: ReadonlyMap<string, string> | ReadonlySet<string>): 'HOLIDAY' | 'WEEK_OFF' | null {
  if (holidays.has(key)) return 'HOLIDAY';
  if (policy.weeklyOff.includes(weekdayOfKey(key))) return 'WEEK_OFF';
  return null;
}

/** How many minutes after opening a mark-in came — what "Late" means in minutes. */
export function lateMinutesOf(inAt: Date, policy: AttendancePolicy): number {
  return Math.max(0, clockMinutes(istClockOf(inAt)) - clockMinutes(policy.opensAt));
}

/** Whether a mark-in at this instant is late by the rules: after opening plus the grace, on a working day. */
export function isLateMarkIn(at: Date, policy: AttendancePolicy, calendar: 'HOLIDAY' | 'WEEK_OFF' | null): boolean {
  if (calendar) return false;
  return clockMinutes(istClockOf(at)) > clockMinutes(policy.opensAt) + policy.lateAfterMinutes;
}

/** Hours between two instants, to one decimal. */
export function hoursBetween(from: Date, to: Date): number {
  return Math.max(0, Math.round(((to.getTime() - from.getTime()) / 3_600_000) * 10) / 10);
}

// ---------------------------------------------------------------------------
// What a screen reads
// ---------------------------------------------------------------------------

export interface GeoPoint {
  lat: number;
  lng: number;
  /** Metres, when the device said. */
  accuracy: number | null;
}

export interface AttendanceDayView {
  day: string;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  /** Null for today before a mark, a future day, or a day before attendance began. */
  status: AttendanceStatus | null;
  label: string;
  source: AttendanceSource | null;
  inAt: string | null;
  outAt: string | null;
  late: boolean;
  /** Minutes after the shift opened, when late. */
  lateMinutes: number;
  hours: number | null;
  inWhere: GeoPoint | null;
  outWhere: GeoPoint | null;
  /** The office's reason, or the leave's. */
  note: string | null;
  setBy: string | null;
  holiday: string | null;
  leaveId: string | null;
  /** Overtime the office recorded on the day, in minutes. */
  overtimeMinutes: number;
  /** A fine the office put on the day, in rupees. */
  fineAmount: number;
}

export interface AttendanceCounts {
  /** Working days of the month up to today (or the whole month, once over). */
  workingDays: number;
  present: number;
  halfDay: number;
  absent: number;
  /** Paid leave. */
  leave: number;
  unpaidLeave: number;
  holiday: number;
  weekOff: number;
  late: number;
  /** Today and the days still to come, not yet anything. */
  pending: number;
  overtimeMinutes: number;
  /** Hours between mark-in and mark-out, summed. */
  hoursWorked: number;
  /** Fines the office put on days, in rupees. */
  dayFines: number;
}

/** Counts over a month's days — the same arithmetic for one person's page, the register and the salary. */
export function countAttendance(days: readonly Pick<AttendanceDayView, 'status' | 'late' | 'overtimeMinutes' | 'fineAmount' | 'hours'>[]): AttendanceCounts {
  const counts: AttendanceCounts = { workingDays: 0, present: 0, halfDay: 0, absent: 0, leave: 0, unpaidLeave: 0, holiday: 0, weekOff: 0, late: 0, pending: 0, overtimeMinutes: 0, hoursWorked: 0, dayFines: 0 };
  for (const day of days) {
    if (day.late) counts.late += 1;
    counts.overtimeMinutes += day.overtimeMinutes;
    counts.hoursWorked += day.hours ?? 0;
    counts.dayFines += day.fineAmount;
    switch (day.status) {
      case 'PRESENT':
        counts.present += 1;
        counts.workingDays += 1;
        break;
      case 'HALF_DAY':
        counts.halfDay += 1;
        counts.workingDays += 1;
        break;
      case 'ABSENT':
        counts.absent += 1;
        counts.workingDays += 1;
        break;
      case 'LEAVE':
        counts.leave += 1;
        counts.workingDays += 1;
        break;
      case 'UNPAID_LEAVE':
        counts.unpaidLeave += 1;
        counts.workingDays += 1;
        break;
      case 'HOLIDAY':
        counts.holiday += 1;
        break;
      case 'WEEK_OFF':
        counts.weekOff += 1;
        break;
      default:
        counts.pending += 1;
    }
  }
  counts.hoursWorked = Math.round(counts.hoursWorked * 10) / 10;
  counts.dayFines = Math.round(counts.dayFines * 100) / 100;
  return counts;
}

export interface TodayState {
  day: string;
  /** What the calendar says: a working day, or why not. */
  calendar: 'HOLIDAY' | 'WEEK_OFF' | null;
  holiday: string | null;
  status: AttendanceStatus | null;
  inAt: string | null;
  outAt: string | null;
  late: boolean;
  lateMinutes: number;
  hours: number | null;
  /** When this person's shift ends, "HH:MM" — the mark-out reminder reads it. */
  closesAt: string;
  canMarkIn: boolean;
  canMarkOut: boolean;
  /** Why the mark-in button is off, when it is. */
  why: string | null;
  /** Where they mark in: null for a driver or a desk with no branch. */
  branch: { name: string; anywhere: boolean; located: boolean; radiusM: number } | null;
}

/** How far from a branch's spot a mark-in is taken unless the office sets otherwise. */
export const BRANCH_RADIUS_M = 200;
export const BRANCH_RADIUS_MAX = 5_000;
export const BRANCH_NAME_MAX = 60;

export interface BranchView {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  radiusM: number;
  /** Marks from anywhere — location still asked for and kept. */
  anywhere: boolean;
  people: Array<{ id: string; name: string; role: string; book: string }>;
}

export interface LeaveView {
  id: string;
  user: { id: string; name: string; role: string; book: string; branch?: string | null };
  from: string;
  to: string;
  days: number;
  kind: LeaveKind;
  kindLabel: string;
  reason: string;
  status: LeaveStatus;
  statusLabel: string;
  askedAt: string;
  answeredBy: string | null;
  answeredAt: string | null;
  answerNote: string | null;
  /** Whether the viewer may take it back: their own, still waiting or approved for days to come. */
  canCancel: boolean;
}

export interface HolidayView {
  id: string;
  day: string;
  name: string;
  weekday: number;
}

export interface MyAttendance {
  person: { id: string; name: string; role: string; book: string };
  month: string;
  policy: AttendancePolicy;
  today: TodayState;
  days: AttendanceDayView[];
  counts: AttendanceCounts;
  leaves: LeaveView[];
}

export interface AttendanceSummary {
  marks: boolean;
  manages: boolean;
  /** Sets salaries and pays: the head-admin desks. */
  salary: boolean;
  startsOn: string;
  today: TodayState | null;
  /** For a manager: leave requests waiting for an answer. */
  waitingLeaves: number;
  /** For a manager: people with nothing on today yet, once the office has opened on a working day. */
  notMarked: number;
}

export interface TodayBoardRow {
  user: { id: string; name: string; role: string; book: string; branch?: string | null };
  status: AttendanceStatus | null;
  source: AttendanceSource | null;
  inAt: string | null;
  outAt: string | null;
  late: boolean;
  lateMinutes: number;
  hours: number | null;
  inWhere: GeoPoint | null;
  note: string | null;
  overtimeMinutes: number;
  fineAmount: number;
}

/** One day for everyone — today, or any day since attendance began, the way Salary Box lets the owner mark a date. */
export interface TodayBoard {
  day: string;
  calendar: 'HOLIDAY' | 'WEEK_OFF' | null;
  holiday: string | null;
  policy: AttendancePolicy;
  counts: { people: number; in: number; out: number; late: number; onLeave: number; notMarked: number; half: number; absent: number; unpaidLeave: number };
  rows: TodayBoardRow[];
}

export interface RegisterRow {
  user: { id: string; name: string; role: string; book: string; branch?: string | null };
  /** One entry per day of the month, in order. */
  days: (AttendanceStatus | null)[];
  late: boolean[];
  counts: AttendanceCounts;
}

export interface AttendanceRegister {
  month: string;
  days: string[];
  holidays: Record<string, string>;
  policy: AttendancePolicy;
  rows: RegisterRow[];
  /** Everyone's counts added up — the register's last line. */
  totals: AttendanceCounts;
}
